import React, { useEffect, useMemo, useState, useRef } from "react";
import { adminGet, adminPost } from "../common/api/api";

const ROLE_STYLES = {
  "super-admin": "text-yellow-400 bg-yellow-400/10 border-yellow-400/30",
  "admin": "text-[#00d4ff] bg-[rgba(0,212,255,0.1)] border-[rgba(0,212,255,0.3)]",
  "user": "text-[#4a5568] bg-white/[0.04] border-white/[0.1]",
};

const ADMIN_SECTIONS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "products", label: "Products" },
  { key: "categories", label: "Categories" },
  { key: "orders", label: "Orders" },
  { key: "payments", label: "Payments" },
  { key: "coupons", label: "Coupons" },
  { key: "reviews", label: "Reviews" },
  { key: "users", label: "Users" },
  { key: "settings", label: "Settings" },
  { key: "homepage", label: "Home Page" },
  { key: "management", label: "Admin Management" },
  { key: "payment-settings", label: "Payment Settings" },
];

const normalizeAccess = (access) => ({
  mode: access?.mode === "full" ? "full" : "custom",
  allowedSections: Array.isArray(access?.allowedSections) ? access.allowedSections : [],
  deniedSections: Array.isArray(access?.deniedSections) ? access.deniedSections : [],
});

const AdminManagement = () => {
  const [allUsers, setAllUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dropOpen, setDropOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [assignRole, setAssignRole] = useState("admin");
  const [allowedSections, setAllowedSections] = useState(["dashboard", "products"]);
  const [deniedSections, setDeniedSections] = useState([]);
  const [saving, setSaving] = useState(false);
  const dropRef = useRef();

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await adminGet("/allusers");
      const list = Array.isArray(data) ? data : [];
      setAllUsers(list);
      setAdmins(list.filter((u) => u.role && u.role !== "user"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filteredUsers = allUsers.filter((u) =>
    !search ||
    (u.name || "").toLowerCase().includes(search.toLowerCase()) ||
    (u.email || "").toLowerCase().includes(search.toLowerCase())
  );

  const applyUserAccess = (user) => {
    setSelectedUser(user);
    setAssignRole(user.role || "admin");
    const access = normalizeAccess(user.adminAccess || {});
    setAllowedSections(access.allowedSections.length ? access.allowedSections : ["dashboard", "products"]);
    setDeniedSections(access.deniedSections);
  };

  const clearForm = () => {
    setSelectedUser(null);
    setSearch("");
    setAssignRole("admin");
    setAllowedSections(["dashboard", "products"]);
    setDeniedSections([]);
  };

  const toggleAllowed = (key) => {
    setAllowedSections((prev) => prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]);
  };

  const toggleDenied = (key) => {
    setDeniedSections((prev) => prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]);
  };

  const saveRole = async () => {
    if (!selectedUser) return;
    if (assignRole === "admin" && allowedSections.length === 0) {
      alert("Select at least one allowed section for admin.");
      return;
    }
    setSaving(true);
    try {
      await adminPost("/updateuserrole", {
        userId: selectedUser._id,
        role: assignRole,
        adminAccess: assignRole === "admin"
          ? { mode: "custom", allowedSections, deniedSections }
          : undefined,
      });
      clearForm();
      fetchUsers();
    } finally {
      setSaving(false);
    }
  };

  const removeRole = async (userId) => {
    if (!window.confirm("Remove admin role from this user?")) return;
    await adminPost("/updateuserrole", {
      userId,
      role: "user",
      adminAccess: { mode: "custom", allowedSections: [], deniedSections: [] },
    });
    fetchUsers();
  };

  const inputClass = "bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-[#e2e8f0] placeholder-[#4a5568] focus:outline-none focus:border-[#00d4ff]/60 text-sm w-full";

  const currentSummary = useMemo(() => {
    if (!selectedUser) return "";
    if (assignRole === "super-admin") return "Full access";
    return `Allowed: ${allowedSections.length} · Denied: ${deniedSections.length}`;
  }, [selectedUser, assignRole, allowedSections.length, deniedSections.length]);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Admin Management</h1>
        <p className="text-sm text-[#4a5568] mt-0.5">Give super-admin full control and set selected or denied access for admin accounts.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {["super-admin", "admin", "user"].map((role) => (
          <div key={role} className={`p-4 rounded-xl border text-center ${ROLE_STYLES[role]}`}>
            <p className="text-2xl font-bold">
              {role === "user" ? allUsers.length : admins.filter((a) => a.role === role).length}
            </p>
            <p className="text-xs mt-1 opacity-80">{role === "super-admin" ? "Super Admins" : role === "admin" ? "Admins" : "Total Users"}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-white/[0.08] bg-[#0a0f1e] p-5 space-y-4">
        <div>
          <h3 className="text-sm font-bold text-white">Assign Role & Access</h3>
          <p className="text-xs text-[#4a5568] mt-1">Admins can get selected sections; super-admin always keeps full access.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.6fr_0.8fr] gap-3 items-start">
          <div className="relative" ref={dropRef}>
            <label className="text-xs text-[#4a5568] mb-1.5 block">Search & Select User</label>
            <div className="relative">
              <input
                type="text"
                value={selectedUser ? `${selectedUser.name || selectedUser.email} (${selectedUser.email})` : search}
                placeholder="Type name or email..."
                className={inputClass}
                onFocus={() => setDropOpen(true)}
                onChange={(e) => { setSearch(e.target.value); setSelectedUser(null); setDropOpen(true); }}
              />
              {dropOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.6)] z-50 max-h-[220px] overflow-y-auto" style={{ background: "rgba(8,16,32,0.98)", border: "1px solid rgba(0,212,255,0.2)" }}>
                  {filteredUsers.length === 0 ? (
                    <div className="px-4 py-3 text-xs text-[#4a5568]">No users found</div>
                  ) : filteredUsers.map((u) => (
                    <button
                      key={u._id}
                      type="button"
                      onClick={() => { applyUserAccess(u); setDropOpen(false); setSearch(""); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[rgba(0,212,255,0.08)] transition-colors border-b border-white/[0.04] last:border-0"
                    >
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#00d4ff] to-[#7c3aed] flex items-center justify-center text-[#060c18] text-xs font-bold shrink-0">
                        {(u.name || u.email || "?")[0].toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-[#e2e8f0] text-xs font-medium truncate">{u.name || "(no name)"}</p>
                        <p className="text-[#4a5568] text-[11px] truncate">{u.email}</p>
                      </div>
                      <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full border shrink-0 ${ROLE_STYLES[u.role || 'user']}`}>{u.role || 'user'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="text-xs text-[#4a5568] mb-1.5 block">Assign Role</label>
            <select className={inputClass} style={{ background: "#0d1527" }} value={assignRole} onChange={(e) => setAssignRole(e.target.value)}>
              <option value="admin">Admin</option>
              <option value="super-admin">Super Admin</option>
              <option value="user">Remove Role (User)</option>
            </select>
          </div>

          <button onClick={saveRole} disabled={!selectedUser || saving} className="px-5 py-2 bg-gradient-to-r from-[#00d4ff] to-[#0099cc] text-[#060c18] font-bold rounded-lg text-sm hover:opacity-90 transition-opacity cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed h-[42px] self-end">
            {saving ? "Saving..." : "Save Access"}
          </button>
        </div>

        {selectedUser && (
          <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4 space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-xs text-[#a0aec0]">
              <span className="font-semibold text-white">{selectedUser.name || selectedUser.email}</span>
              <span className={`px-1.5 py-0.5 rounded-full border text-[10px] ${ROLE_STYLES[selectedUser.role || 'user']}`}>current: {selectedUser.role || 'user'}</span>
              <span>{currentSummary}</span>
            </div>

            {assignRole === "admin" && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-white">Allowed Access</h4>
                    <button type="button" onClick={() => setAllowedSections(ADMIN_SECTIONS.map((section) => section.key))} className="text-[11px] text-[#00d4ff] underline">Allow all</button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {ADMIN_SECTIONS.map((section) => (
                      <button
                        key={section.key}
                        type="button"
                        onClick={() => toggleAllowed(section.key)}
                        className={`px-3 py-2 rounded-lg border text-xs text-left transition-colors ${allowedSections.includes(section.key) ? "border-[rgba(0,212,255,0.3)] bg-[rgba(0,212,255,0.12)] text-[#00d4ff]" : "border-white/[0.08] bg-white/[0.03] text-[#a0aec0] hover:border-white/[0.18]"}`}
                      >
                        {allowedSections.includes(section.key) ? "✓ " : ""}{section.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-white">Denied Access</h4>
                    <button type="button" onClick={() => setDeniedSections([])} className="text-[11px] text-red-400 underline">Clear all</button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {ADMIN_SECTIONS.map((section) => (
                      <button
                        key={section.key}
                        type="button"
                        onClick={() => toggleDenied(section.key)}
                        className={`px-3 py-2 rounded-lg border text-xs text-left transition-colors ${deniedSections.includes(section.key) ? "border-red-400/30 bg-red-400/10 text-red-400" : "border-white/[0.08] bg-white/[0.03] text-[#a0aec0] hover:border-white/[0.18]"}`}
                      >
                        {deniedSections.includes(section.key) ? "✕ " : ""}{section.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {assignRole === "super-admin" && (
              <div className="rounded-xl border border-yellow-400/20 bg-yellow-400/5 p-4 text-sm text-yellow-200">
                Super-admin gets full access automatically. No section restrictions are applied.
              </div>
            )}

            {assignRole === "user" && (
              <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-4 text-sm text-[#a0aec0]">
                Role will be removed and all admin access will be cleared.
              </div>
            )}
          </div>
        )}
      </div>

      <div>
        <h3 className="text-sm font-bold text-white mb-3">Current Admins ({admins.length})</h3>
        {loading ? (
          <div className="text-[#4a5568] py-10 text-center">Loading...</div>
        ) : admins.length === 0 ? (
          <div className="py-10 text-center text-[#4a5568] border border-white/[0.06] rounded-xl">No admins yet. Use the form above to assign a user as admin.</div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-white/[0.08]">
            <div className="min-w-[760px]">
              <div className="grid grid-cols-[40px_1fr_1.5fr_120px_1.5fr_150px] gap-3 px-4 py-3 bg-white/[0.04] text-[#4a5568] text-xs font-bold uppercase tracking-wider">
                <span>#</span><span>Name</span><span>Email</span><span>Role</span><span>Access</span><span>Actions</span>
              </div>
              {admins.map((admin, i) => {
                const access = normalizeAccess(admin.adminAccess || {});
                const accessLabel = admin.role === "super-admin"
                  ? "Full access"
                  : `${access.allowedSections.length} allowed · ${access.deniedSections.length} denied`;
                return (
                  <div key={admin._id} className="grid grid-cols-[40px_1fr_1.5fr_120px_1.5fr_150px] gap-3 items-center px-4 py-3.5 border-t border-white/[0.05] hover:bg-white/[0.02] transition-colors">
                    <span className="text-xs text-[#4a5568]">{i + 1}</span>
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00d4ff] to-[#7c3aed] flex items-center justify-center text-[#060c18] text-xs font-black shrink-0">{(admin.name || admin.email || "?")[0].toUpperCase()}</div>
                      <span className="text-[#e2e8f0] text-sm truncate">{admin.name || "(no name)"}</span>
                    </div>
                    <span className="text-[#a0aec0] text-sm truncate">{admin.email}</span>
                    <div>
                      <select
                        value={admin.role}
                        onChange={(e) => {
                          if (e.target.value === "user") return removeRole(admin._id);
                          applyUserAccess(admin);
                          setAssignRole(e.target.value);
                        }}
                        className="bg-transparent border text-xs px-2 py-1 rounded-lg cursor-pointer focus:outline-none"
                        style={{ borderColor: admin.role === 'super-admin' ? 'rgba(250,204,21,0.4)' : 'rgba(0,212,255,0.3)', color: admin.role === 'super-admin' ? '#facc15' : '#00d4ff' }}
                      >
                        <option value="super-admin" style={{ background: '#0d1527', color: '#facc15' }}>super-admin</option>
                        <option value="admin" style={{ background: '#0d1527', color: '#00d4ff' }}>admin</option>
                      </select>
                    </div>
                    <div className="text-xs text-[#a0aec0] truncate">{accessLabel}</div>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => { applyUserAccess(admin); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="text-xs px-3 py-1 rounded-lg border border-[rgba(0,212,255,0.2)] text-[#00d4ff] hover:bg-[rgba(0,212,255,0.08)] transition-all cursor-pointer w-fit">Edit Access</button>
                      <button onClick={() => removeRole(admin._id)} className="text-xs px-3 py-1 rounded-lg border border-red-500/20 text-red-400 hover:bg-red-500/10 transition-all cursor-pointer w-fit">Remove Role</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminManagement;
